import { bearerFetcher, MatsuriError, useAuthBearerFetch } from "matsuri-hooks"
import { useAuth } from "m2m-components/storage/useM2mAuth"
import { useEffect, useMemo } from "react"
import { localStorageTtl } from "@/utils/localStorageTtl"
import { m2mCleaning } from "@/endpoints/m2m-cleaning"
import { m2mCleaning_cleanerV2 } from "@/endpoints/m2m-cleaning.cleaner-v2"
import { imageCompressToWebp } from "@/utils/image"
import { uploadBlobWithPresignedPut } from "@/utils/presignedUpload"

export interface KeyboxNumberResp {
    isAvailable: boolean
    isRotated: boolean
    keyboxNumber: string
    currentKeyboxNumber?: string
    photoUrl?: string
}

export type CachedKeyboxNumber = Pick<
    KeyboxNumberResp,
    "currentKeyboxNumber" | "keyboxNumber"
>

const keyboxNumberCacheKey = (cleaningId: string) => {
    // キャッシュを無理やりinvalidateしたければバージョンを書き換えてください
    return `${cleaningId}-keyboxnumber-v2`
}

const keyboxNumberStorageApi = {
    getKeyboxNumber: (
        cleaningId: string | undefined
    ): CachedKeyboxNumber | undefined => {
        if (!cleaningId) {
            return undefined
        }
        const item = localStorageTtl.getItem(keyboxNumberCacheKey(cleaningId))
        if (!item) {
            return undefined
        }
        return JSON.parse(item)
    },
    setKeyboxNumber: (
        cleaningId: string | undefined,
        resp: CachedKeyboxNumber
    ) => {
        if (!cleaningId) {
            return
        }

        localStorageTtl.setItem(
            keyboxNumberCacheKey(cleaningId),
            JSON.stringify(resp),
            // キャッシュは短めに設定
            // これはキャッシュしている間に予約状況が変わり予約が変更になる場合、設定するべき番号が変更になる場合があるため
            1 * 60 * 60 * 1000
        )
    }
}

interface KeyboxNumberWithLoadingTrue extends Partial<KeyboxNumberResp> {
    isLoading: true
    error: undefined
}

interface KeyboxNumberWithLoadingFalse extends KeyboxNumberResp {
    isLoading: false
    error: undefined
    refetch: ReturnType<typeof useAuthBearerFetch<KeyboxNumberResp>>["refetch"]
}

interface KeyboxNumberWithError extends Partial<KeyboxNumberResp> {
    isLoading: false
    error: MatsuriError
}

type KeyboxNumberWithLoading =
    | KeyboxNumberWithLoadingTrue
    | KeyboxNumberWithLoadingFalse
    | KeyboxNumberWithError

/**
 * 次に予約がない場合は
 * 鍵番号(keyboxNumber,currentKeyboxNumberフィールド)が
 * API呼ぶたびに変わってしまうので自前でキャッシュを取る
 */
export const useKeyboxNumber = (
    cleaningId: string
): KeyboxNumberWithLoading => {
    const { token } = useAuth()

    const endpoint = m2mCleaning_cleanerV2.getKeyboxNumber({ cleaningId })

    const { data, ...resp } = useAuthBearerFetch<KeyboxNumberResp>(
        token,
        endpoint,
        {
            swrConfig: {
                shouldRetryOnError: false
            }
        }
    )

    const cached = keyboxNumberStorageApi.getKeyboxNumber(cleaningId)
    const dataWithCache = cached ?? data

    useEffect(() => {
        if (!data || cached) {
            return
        }

        keyboxNumberStorageApi.setKeyboxNumber(cleaningId, data)
    }, [cleaningId, cached, data])

    const keyboxNumberResp: KeyboxNumberResp | undefined = useMemo(
        () =>
            data && dataWithCache
                ? {
                      // APIから取得。キャッシュはしない
                      isAvailable: data.isAvailable,
                      isRotated: data.isRotated,
                      photoUrl: data.photoUrl,
                      // APIの値が変化しうるのでキャッシュする
                      keyboxNumber: dataWithCache.keyboxNumber,
                      currentKeyboxNumber: dataWithCache.currentKeyboxNumber
                  }
                : undefined,
        [data, dataWithCache]
    )

    if (resp.isLoading) {
        return {
            isLoading: true,
            error: undefined
        }
    }
    if (resp.error) {
        return {
            isLoading: false,
            error: resp.error
        }
    }

    if (!keyboxNumberResp) {
        console.warn("本来到達できないパターン")
        return {
            isLoading: true,
            error: undefined
        }
    }

    return {
        refetch: resp.refetch,
        ...keyboxNumberResp,
        isLoading: false,
        error: undefined
    }
}

export const requestGetKeyboxState = async (
    token: string,
    cleaningId: string
): Promise<{
    shouldRotate?: boolean
    error?: string
}> => {
    // eslint-disable-next-line no-restricted-globals -- 404を正常系として扱う特殊なエラーハンドリングのためfetchを直接使用
    const response = await fetch(
        m2mCleaning.cleanerV2.getKeyboxNumber({ cleaningId }),
        {
            headers: {
                Authorization: `Bearer ${token}`
            },
            method: "GET"
        }
    )
    if (!response.ok) {
        // 404はkeybox変更対象物件でないのでエラーにしない
        if (response.status === 404) {
            return { shouldRotate: false }
        }

        return {
            error: "KeyboxNumberInconsistency"
        }
    }

    const resp: KeyboxNumberResp = await response.json()

    return {
        shouldRotate: resp.isAvailable && !resp.isRotated
    }
}

export const requestRotateKeyboxNumber = async (
    token: string,
    cleaningId: string,
    keyboxNumber: string,
    photoFile?: File
): Promise<void> => {
    let uploadId: string | undefined

    if (photoFile) {
        const compressed = await imageCompressToWebp(photoFile, {
            quality: 0.85,
            maxWidth: 1500,
            maxHeight: 1500
        })

        const issueUrl = m2mCleaning_cleanerV2.issueKeyboxPictureUploadUrl({
            cleaningId
        })
        const result = await uploadBlobWithPresignedPut({
            token,
            issueUrl,
            blob: compressed
        })
        uploadId = result.uploadId
    }

    const completeResult = await bearerFetcher(
        token,
        m2mCleaning_cleanerV2.completeKeyboxRotation({ cleaningId }),
        {
            method: "POST",
            body: JSON.stringify({ keyboxNumber, uploadId })
        }
    )
    if (completeResult.error) {
        throw new Error("RequestError")
    }
}
